// Types for field schedule (referee shifts, events, private parties)

export type ScheduleEntryType = 'referee_shift' | 'event' | 'private_party';

export interface FieldScheduleEntry {
  id: string;
  fieldId: string;
  entryType: ScheduleEntryType;
  date: string;
  startTime: string;
  endTime: string;

  // For referee_shift (backward compatibility with single referee)
  refereeId?: string;
  refereeName?: string;

  // For multiple referees (all entry types)
  refereeIds?: string[];
  refereeNames?: string[];

  gameType?: 'airsoft' | 'paintball' | 'speedball';

  // For event and private_party
  title?: string;
  description?: string;

  // Common
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduleEntryRequest {
  entryType: ScheduleEntryType;
  startTime: string;
  endTime: string;
  refereeId?: string;
  refereeIds?: string[];
  gameType?: string;
  title?: string;
  description?: string;
  notes?: string;
}

export interface UpdateScheduleEntryRequest {
  entryType: ScheduleEntryType;
  startTime: string;
  endTime: string;
  refereeId?: string;
  refereeIds?: string[];
  gameType?: string;
  title?: string;
  description?: string;
  notes?: string;
}

export interface DaySchedule {
  date: Date;
  dayName: string;
  dayNumber: number;
  monthName: string;
  entries: FieldScheduleEntry[];
  isToday: boolean;
}

// Helper function to get week start (Monday)
export const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

// Helper function to format time from date string
// Форматирует время в американском формате (12h AM/PM)
export const formatTime = (dateTimeString: string): string => {
  // Извлекаем время напрямую из строки без конвертации таймзоны
  // Формат: "2024-12-20T09:00:00" или "2024-12-20T09:00:00Z"
  const timePart = dateTimeString.split('T')[1];
  if (!timePart) return '12:00 AM';

  const timeOnly = timePart.substring(0, 5); // HH:MM
  const [hours, minutes] = timeOnly.split(':').map(Number);

  // Американский формат: 12-часовой с AM/PM
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12; // 0 -> 12, 13 -> 1, etc.
  const minutesStr = minutes.toString().padStart(2, '0');

  return `${hours12}:${minutesStr} ${period}`;
};

// Colors for entry types
export const ENTRY_TYPE_COLORS: Record<ScheduleEntryType, string> = {
  referee_shift: '#00d4ff', // синий
  event: '#4CAF50',         // зелёный
  private_party: '#FF9800'  // оранжевый
};

// Labels for entry types
export const ENTRY_TYPE_LABELS: Record<ScheduleEntryType, string> = {
  referee_shift: 'Referee Shift',
  event: 'Event',
  private_party: 'Private Party'
};
